import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { MdHelpOutline, MdAutoFixNormal } from "react-icons/md";
import ProcessingDataAnimation from "./utils/ProcessingDataAnimation";
import { usePlatform } from "components/utils/PlatformContext";

interface UploadCsvProps {
  file: string[];
  setFile: React.Dispatch<React.SetStateAction<string[]>>;
  setStage: React.Dispatch<React.SetStateAction<number>>;
  onImportIndividualStudent: () => void;
}

const UploadCsv: React.FC<UploadCsvProps> = ({
  file,
  setFile,
  setStage,
  onImportIndividualStudent,
}) => {
  // States definition
  const [isDragging, setIsDragging] = useState(false);
  const [finishedValidation, setFinishedValidation] = useState(false);

  // Get the platform context
  const { platform, dataRequirements } = usePlatform();

  /**
   * If we finished the CSV validation, wait some seconds before proceed
   */
  useEffect(() => {
    if (finishedValidation) {
      const timer = setTimeout(() => {
        // Once finished, move to the second stage
        setFinishedValidation(false);
        setStage(2);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [finishedValidation]);

  /**
   * Function used on drop the area
   * @param event
   */
  const handleDrop = (event: React.DragEvent) => {
    // Prevent opening the file on another tab
    event.preventDefault();

    // Set dragging state to false
    setIsDragging(false);

    // Get the list of files dropped
    const droppedFiles = event.dataTransfer.files;

    // Check if the drop contain files
    if (droppedFiles.length > 0) {
      // Transform into an array
      const newFiles = Array.from(droppedFiles);

      // And use just the first file
      processCsvUpload(newFiles[0]);
    }
  };

  /**
   * Function used to control drag and drop behaviour when leaving
   * @param event
   */
  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  /**
   * Function used to prevent default action when dragging over the element
   * @param event
   */
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Function used to control drag and drop behaviour when entering
   * @param event
   */
  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  /**
   * Function used for manage file uploading
   */
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Get the file
    const file = event.target.files?.[0];

    // Process the file
    processCsvUpload(file);
  };

  /**
   * Function to process the csv upload
   * @param {File | undefined} file
   */
  const processCsvUpload = (file: File | undefined) => {
    // Check if there is an inputted file
    if (!file) {
      // alert("No file was selected.");
      return;
    }

    // Check if the of the uploaded file is the defined in the
    // formats list allowed for this placeholder
    if (!["text/csv", "application/vnd.ms-excel"].includes(file.type)) {
      alert(
        `Format "${file.type}" is not allowed. Please try with another file.`
      );
      return;
    }

    // Perform the loading
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      // Get the string
      const text = event.target ? (event.target.result as string) : "";
      // Split content into lines
      const lines = text.split(/\r\n|\n/);
  
      // Update the file
      setFile(lines);
  
      // And set the finished state
      setFinishedValidation(true);
    };
    reader.readAsText(file as any);
  };

  /**
   * Function used to generate the students csv file
   */
  const handleDownloadCSVTemplate = () => {
    // Define the header as a list
    const headerArray = [
      "First name",
      "Last name",
      "DOB",
      "Gender",
      "Class name",
      ...(dataRequirements.includes("heritage") ? ["Aboriginal or Torres Strait Islander origin"]: []),
      "User name",
      "Notes",
    ];

    // And the example as well
    const exampleArray = [
      "Example",
      "Student",
      "(12/08/2012) optional",
      "(Male or Female or Other) optional",
      "4A Digital technology",
      ...(dataRequirements.includes("heritage") ? ["(Y or N) optional"]: []),
      "If blank a user name will be generated automatically for new students",
      "optional",
    ];

    // Transform into strings
    const headerString = headerArray.join(",");
    const exampleString = exampleArray.join(",");

    // Define the csv header of the table
    const csvFile = `${headerString}\n${exampleString}`;

    // Create a Blob with the CSV data
    const blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });

    // Create a link element
    const link = document.createElement("a");

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);

    // Set link attributes
    link.href = url;
    link.download = `${platform}-bulk-student-import-template.csv`;

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // And finally revoke the URL to free up memory
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
      transition={{ duration: 0.1, ease: "easeOut" }}
      className="self-stretch w-[437px] flex-1 flex-col justify-start items-start gap-12 inline-flex overflow-y-auto custom-scroll"
    >
      {file && file.length > 0 ? (
        <ProcessingDataAnimation
          finishedValidation={finishedValidation}
          processingText="Validating data from import file"
        />
      ) : (
        <>
          <div className="self-stretch h-fit flex-col justify-start items-start gap-8 inline-flex">
            <div className="self-stretch justify-start items-center gap-1 inline-flex">
              <div className="">
                <span className="text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                  Upload a CSV file with new or existing students in{" "}
                </span>
                <span
                  className="text-secondary-600 text-sm font-normal font-sans leading-[17px] cursor-pointer hover:underline"
                  onClick={handleDownloadCSVTemplate}
                >
                  this format
                </span>
              </div>
              <div className="w-3 h-3 relative">
                <MdHelpOutline className="w-full h-full fill-secondary-600" />
              </div>
            </div>
            <div className="self-stretch p-2 bg-accent-50 rounded-xl justify-center items-center gap-2 inline-flex">
              <div className="self-stretch relative">
                <MdAutoFixNormal className="w-5 h-5 fill-accent-600" />
              </div>
              <div className="grow shrink basis-0 leading-[22px]">
                <span className="text-accent-600 text-sm font-semibold font-sans">
                  Pro tip:{" "}
                </span>
                <span className="text-accent-600 text-sm font-normal font-sans">
                  Download our{" "}
                </span>
                <span
                  className="text-accent-600 hover:text-accent-700 cursor-pointer text-sm font-normal font-sans underline"
                  onClick={handleDownloadCSVTemplate}
                >
                  ready-to-use template{" "}
                </span>
                <span className="text-accent-600 text-sm font-normal font-sans">
                  to get started. The template includes a header row showing you
                  exactly where each piece of information goes. Just add your
                  student details in the rows below, save the file, and upload
                  it here.
                </span>
              </div>
            </div>
            <div className="h-fit flex-col justify-start items-start gap-3 inline-flex">
              <div className="self-stretch h-fit justify-start items-center gap-1 inline-flex">
                <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
                <div className="text-neutral-700 text-sm font-normal font-sans leading-[21px]">
                  {dataRequirements.includes("heritage") ? "8" : "7"} Columns
                </div>
              </div>
              <div className="self-stretch justify-start items-center gap-1 inline-flex">
                <div className="w-3 h-full pt-2 flex-col justify-start items-center gap-2 inline-flex">
                  <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
                </div>
                <div className="text-neutral-700 text-sm font-normal font-sans leading-relaxed">
                  In this order: First Name, Last Name, D.O.B. (optional),
                  Gender (optional), Class Name
                  {dataRequirements.includes("heritage") &&
                    ", Aboriginal or Torres Strait Islander origin? (optional)"}
                  , User Name (optional), Notes (optional)
                </div>
              </div>
              <div className="self-stretch justify-start items-center gap-1 inline-flex">
                <div className="w-2 h-2 m-0.5 bg-neutral-700 rounded-full" />
                <div className="text-neutral-700 text-sm font-normal font-sans leading-[21px]">
                  Maximum 1000 rows
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch h-fit flex-col justify-start items-start gap-3 flex">
            <div
              className={`self-stretch h-fit px-14 py-[40px] bg-neutral-25 rounded-xl border ${
                isDragging
                  ? "border-secondary-500 border-solid"
                  : "border-neutral-500 border-dashed"
              } justify-center items-center gap-1 flex relative`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
            >
              <div className="w-3 h-3 relative inline-flex items-center justify-center">
                <MdHelpOutline className="w-full h-full fill-secondary-600" />
              </div>
              <div className="grow shrink basis-0">
                <span className="text-neutral-700 text-sm font-normal font-sans leading-[21px] select-none">
                  Drop CSV file here or{" "}
                </span>
                <label
                  className="text-secondary-600 text-sm font-semibold font-sans leading-[17px] cursor-pointer hover:underline select-none"
                  htmlFor="csv-input"
                >
                  select from computer
                </label>
                <input
                  type="file"
                  accept="text/csv,application/vnd.ms-excel"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-input"
                />
              </div>
              {isDragging && (
                <label
                  htmlFor="csv-input"
                  className="absolute inset-0 w-full h-full opacity-0 rounded-xl border"
                  onDrop={handleDrop}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                />
              )}
            </div>
            <div className="grow shrink basis-0">
              <span className="text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                Looking to import individual students?{" "}
              </span>
              <span
                className="text-secondary-600 hover:text-secondary-700 text-sm font-normal font-sans underline leading-[17px] cursor-pointer select-none"
                onClick={onImportIndividualStudent}
              >
                Click here
              </span>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default UploadCsv;
